import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';

import { ModalInstance, ReactModalOptions, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { CreateExplorerEvent } from '~commonInfra/telemetryEvents/TelemetryEventCreator';
import { ExplorerOpenDetailEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { useIsMounted } from '~src/bootstrap/hooks';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { IDatasourceActions } from '~src/datasource/ServerApi/types/IDatasourceActions';
import { IProjectActions } from '~src/projects/ServerApi/types/IProjectActions';
import { getContentSpaceDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType, isSpaceInfoItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { IWorkbookActions } from '~src/workbook/ServerApi/types/IWorkbookActions';

const DIALOG_WIDTH = TabDialogWidth.width700px;
export const ACTION_DIALOG_TESTID = 'content-detail-dialog';
export const ACTION_DIALOG_ID = 'content-detail-dialog';

export namespace ContentDetailsDialog {

  interface ContentsOwnProps {
    readonly children: React.ReactNode;
    readonly confirmCloseDialog: () => void;
  }

  interface ContentsStateProps {
    readonly routerReactKey: number;
  }

  type ContentsProps = ContentsOwnProps & ContentsStateProps;

  const Contents = (props: ContentsProps) => {
    const isMounted = useIsMounted();
    React.useEffect(
      () => {
        if (isMounted) {
          // The routerReactKey is used in high level HashRouters and triggers rerenders if key changes.
          // We use this to refresh all content in a page without doing a page reload.
          // So we know if an action change was successful if this key has changed and so close the dialog.
          props.confirmCloseDialog();
        }
      },
      // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.routerReactKey]
    );

    return (
      <div style={{display: 'flex', flexDirection: 'column'}}>
       {props.children}
      </div>
    );
  };

  const mapStateToProps: MapStateToProps<ContentsStateProps> = (state: AppState) => ({
    routerReactKey: state.routerSpaceReactKey
  });

  export const ConnectedContents: React.ComponentClass<ContentsOwnProps> =
    connect(mapStateToProps)(Contents);

  interface OptionsProps {
    readonly content: IContentInfo<ExplorableItemType>;
    readonly availableActions: IDatasourceActions|IWorkbookActions|IProjectActions;
    readonly location: string;
    readonly confirmCloseDialog: () => void;
  }
  export function Options(props: OptionsProps): ReactModalOptions {

    const contentType = props.content.contentType;
    let dialogTitle = '';
    let dialogBody: any = undefined;
    if (isSpaceInfoItemType(contentType)) {
      const contentDefinition = getContentSpaceDefinition(contentType);
      dialogTitle = contentDefinition.detailDialogTitle();
      dialogBody = contentDefinition.detailDialogBody;
    }

    if (dialogTitle.length === 0 || _.isUndefined(dialogBody)) {
      throw new Error(contentType + ' is not supported for react detail dialog.');
    }

    return {
      title: dialogTitle,
      testId: ACTION_DIALOG_TESTID,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      dialogWidth: DIALOG_WIDTH,
      dialogStyle: {minWidth: DIALOG_WIDTH + 'px'},
      dialogBodyStyle: {width: DIALOG_WIDTH + 'px'},
      dialogContent:
        <ConnectedContents confirmCloseDialog={props.confirmCloseDialog}>
          {dialogBody(props.content, props.availableActions, props.location)}
        </ConnectedContents>,
      dialogContentStyle: { padding: 0 },
    };
  }

  export function open(content: IContentInfo<ExplorableItemType>, availableActions: IDatasourceActions|IWorkbookActions|IProjectActions, location?: string) {
    let modalInstance: ModalInstance<'confirm'>;

    const confirmCloseDialog = (): void => {
      modalInstance && modalInstance.close('confirm');
    };
    const optionProps: OptionsProps = {
      content,
      availableActions,
      location,
      confirmCloseDialog
    };

    Telemetry.sendEvent<ExplorerOpenDetailEvent>(CreateExplorerEvent<ExplorerOpenDetailEvent>(
      {
        type: TelemetryEvents.ExplorerOpenDetail,
        detailContentId: content.id,
        detailContentType: content.contentType
      }
    ));

    modalInstance = ReactModalService.open(Options(optionProps));
    return modalInstance.result;
  }
}
